export interface LocalAIConfig {
  modelName: string
  modelPath: string
  maxTokens: number
  temperature: number
  topP: number
  cacheSize: number
  enablePersistentCache: boolean
  contextWindowSize: number
  enableStreaming: boolean
}

export interface AIResponse {
  text: string
  finishReason: string
  tokenCount: number
  cached: boolean
  responseTime: number
  intent?: string
  slots?: Record<string, any>
}

export interface ModelInfo {
  name: string
  size: string
  isLoaded: boolean
  isLoading: boolean
  memoryUsage?: number
  loadedAt?: Date
  lastUsed?: Date
}

export interface IntentResult {
  intent: string
  confidence: number
  slots: Record<string, any>
  resolvedText: string
}

interface CacheEntry {
  timestamp: number
  data: AIResponse
  ttl: number
}

interface ContextEntry {
  sessionId: string
  messages: Array<{ role: string; content: string }>
  createdAt: number
  lastAccessed: number
}

export class LocalAIEngine {
  private model: any = null
  private tokenizer: any = null
  private isLoading = false
  private isLoaded = false
  private config: LocalAIConfig
  private loadProgressCallback: ((progress: number, status: string) => void) | null = null
  private responseCache: Map<string, CacheEntry> = new Map()
  private recentRequests: Array<{ timestamp: number; tokens: number }> = []
  private totalTokensProcessed = 0
  private totalRequests = 0
  private modelLoadTime: number | null = null
  private contexts: Map<string, ContextEntry> = new Map()
  private intentPatterns: Record<string, { regex: RegExp; slots: string[] }> = {
    greet: { regex: /^(你好|您好|嗨|哈喽|hello|hi)/i, slots: [] },
    help: { regex: /^(帮助|帮我|请问|怎么|如何)/i, slots: ['action'] },
    login: { regex: /^(登录|账号|密码|登录页)/i, slots: ['account', 'password'] },
    logout: { regex: /^(退出|登出|注销)/i, slots: [] },
    search: { regex: /^(搜索|查找|查询)/i, slots: ['query'] },
    report: { regex: /^(报告|分析|统计|数据)/i, slots: ['type', 'period'] },
    settings: { regex: /^(设置|配置|参数)/i, slots: ['setting', 'value'] },
    order: { regex: /^(订单|下单|购买)/i, slots: ['product', 'quantity'] },
    user: { regex: /^(用户|客户|会员)/i, slots: ['userId', 'action'] },
    finance: { regex: /^(财务|账单|付款|收款)/i, slots: ['amount', 'type'] },
    marketing: { regex: /^(营销|推广|活动)/i, slots: ['campaign', 'channel'] },
    content: { regex: /^(内容|文章|文案)/i, slots: ['topic', 'length'] },
    image: { regex: /^(图片|生成|设计)/i, slots: ['style', 'subject'] },
    voice: { regex: /^(语音|声音|朗读)/i, slots: ['text', 'voice'] },
    recommendation: { regex: /^(推荐|建议|推荐)/i, slots: ['category', 'count'] },
    monitoring: { regex: /^(监控|预警|报警)/i, slots: ['metric', 'threshold'] }
  }

  private static readonly DEFAULT_CONFIG: LocalAIConfig = {
    modelName: 'Qwen/Qwen2-1.5B-Instruct-GGUF',
    modelPath: '',
    maxTokens: 1024,
    temperature: 0.7,
    topP: 0.9,
    cacheSize: 500,
    enablePersistentCache: true,
    contextWindowSize: 20,
    enableStreaming: true
  }

  constructor(config: Partial<LocalAIConfig> = {}) {
    this.config = { ...LocalAIEngine.DEFAULT_CONFIG, ...config }
    this.loadPersistentCache()
    this.loadPersistentContexts()
  }

  setLoadProgressCallback(callback: (progress: number, status: string) => void): void {
    this.loadProgressCallback = callback
  }

  async loadModel(): Promise<ModelInfo> {
    if (this.isLoading || this.isLoaded) {
      return this.getModelInfo()
    }

    this.isLoading = true
    const startTime = Date.now()
    
    try {
      this.updateProgress(0, '正在初始化...')
      
      const { env, AutoModelForCausalLM, AutoTokenizer } = await import('@xenova/transformers')
      
      const hfEndpoint = import.meta.env.VITE_HF_ENDPOINT || 'https://hf-mirror.com'
      env.remoteHost = hfEndpoint
      console.log(`Using HuggingFace endpoint: ${hfEndpoint}`)
      
      this.updateProgress(0.1, '正在下载模型...')
      
      const modelOptions: any = {
        quantized: true,
        device: 'cpu',
        progress_callback: (progress: number) => {
          const adjustedProgress = 0.1 + progress * 0.8
          this.updateProgress(adjustedProgress, `正在加载模型: ${Math.round(progress * 100)}%`)
        }
      }
      
      const hfToken = import.meta.env.VITE_HF_TOKEN
      if (hfToken) {
        modelOptions.hf_token = hfToken
      }
      
      this.model = await AutoModelForCausalLM.from_pretrained(
        this.config.modelName,
        modelOptions
      )
      
      this.updateProgress(0.9, '正在加载分词器...')
      
      this.tokenizer = await AutoTokenizer.from_pretrained(this.config.modelName, {
        ...(hfToken ? { hf_token: hfToken } : {})
      })
      
      this.isLoaded = true
      this.modelLoadTime = Date.now() - startTime
      
      this.updateProgress(1, '模型加载完成！')
      console.log(`Local AI model loaded in ${this.modelLoadTime}ms`)
      
      return this.getModelInfo()
    } catch (error) {
      console.error('Failed to load local AI model:', error)
      this.updateProgress(0, '加载失败')
      throw error
    } finally {
      this.isLoading = false
    }
  }

  private updateProgress(progress: number, status: string): void {
    if (this.loadProgressCallback) {
      this.loadProgressCallback(progress, status)
    }
  }

  async generate(prompt: string, options?: {
    maxTokens?: number
    temperature?: number
    topP?: number
    skipCache?: boolean
    stream?: boolean
    onToken?: (token: string) => void
  }): Promise<AIResponse> {
    const startTime = Date.now()
    
    if (!this.isLoaded) {
      await this.loadModel()
    }

    const requestOptions = {
      maxTokens: options?.maxTokens ?? this.config.maxTokens,
      temperature: options?.temperature ?? this.config.temperature,
      topP: options?.topP ?? this.config.topP
    }

    const cacheKey = this.generateCacheKey(prompt, requestOptions)
    
    if (!options?.skipCache && !options?.stream) {
      const cached = this.getFromCache(cacheKey)
      if (cached) {
        const responseTime = Date.now() - startTime
        return { ...cached, responseTime }
      }
    }

    const inputs = this.tokenizer(prompt, { return_tensor: true })
    
    if (options?.stream && options?.onToken) {
      return this.generateStream(prompt, inputs, requestOptions, cacheKey, startTime, options.onToken)
    }

    // ===== 非流式：老逻辑一次生成 =====
    const callOptions: any = {
      max_new_tokens: requestOptions.maxTokens,
      temperature: requestOptions.temperature,
      top_p: requestOptions.topP,
      do_sample: requestOptions.temperature > 0,
      pad_token_id: this.tokenizer?.eos_token_id ?? 0,
      eos_token_id: this.tokenizer?.eos_token_id
    }
    const outputs = await this.model.generate(inputs.input_ids, callOptions)

    const text = this.tokenizer.decode(outputs[0]?.output_token_ids || outputs[0] || [], { skip_special_tokens: true })
    let responseText = text.substring(prompt.length).trim()
    const qPrefix = prompt.slice(0, 20)
    if (responseText.indexOf(qPrefix) === 0) responseText = responseText.slice(qPrefix.length)
    const tokenCount = (outputs[0]?.length || outputs.length || 0)

    const intentResult = this.recognizeIntent(responseText)

    const response: AIResponse = {
      text: responseText,
      finishReason: 'stop',
      tokenCount,
      cached: false,
      responseTime: Date.now() - startTime,
      intent: intentResult.intent,
      slots: intentResult.slots
    }

    this.addToCache(cacheKey, response)
    this.updateStats(tokenCount)

    return response
  }

  private async generateStream(
    prompt: string,
    inputs: any,
    options: { maxTokens: number; temperature: number; topP: number },
    cacheKey: string,
    startTime: number,
    onToken: (token: string) => void
  ): Promise<AIResponse> {
    // ===== 真实 token 级流式输出（Transformers.js 原生 callback） =====
    let fullText = ''
    const callOptions: any = {
      max_new_tokens: options.maxTokens,
      temperature: options.temperature,
      top_p: options.topP,
      do_sample: options.temperature > 0,
      pad_token_id: this.tokenizer?.eos_token_id ?? 0,
      eos_token_id: this.tokenizer?.eos_token_id,
      callback_function: (outputs: any) => {
        try {
          const tokenText: string = typeof outputs?.token_text === 'string' ? outputs.token_text : ''
          if (tokenText && !tokenText.startsWith('<')) {
            fullText += tokenText
            if (fullText.length % 4 < tokenText.length || fullText.length <= 4) {
              let visibleText = fullText
              const qPrefix = prompt.slice(0, 20)
              if (visibleText.indexOf(qPrefix) === 0) {
                visibleText = visibleText.slice(qPrefix.length)
              }
              onToken(visibleText)
            }
          }
        } catch (_) {
          // 忽略单 token 回调错误
        }
      }
    }

    const outs = await this.model.generate(inputs.input_ids, callOptions)

    // 兜底：如果 callback 没被调用（特殊模型），就手动 decode
    if (!fullText || fullText.length === 0) {
      fullText = this.tokenizer.decode(outs[0]?.output_token_ids || outs[0] || [], { skip_special_tokens: true })
    }

    // 去前缀（去掉 prompt 重复）
    let responseText = fullText
    const qPrefix = prompt.slice(0, 20)
    if (responseText.indexOf(qPrefix) === 0) {
      responseText = responseText.slice(qPrefix.length)
    }
    responseText = responseText.trim()

    // 最终回调一次完整内容
    try { onToken(responseText) } catch (_) {}

    const tokenCount = (outs[0]?.length || outs.length || 0)

    const intentResult = this.recognizeIntent(responseText)

    const response: AIResponse = {
      text: responseText,
      finishReason: 'stop',
      tokenCount,
      cached: false,
      responseTime: Date.now() - startTime,
      intent: intentResult.intent,
      slots: intentResult.slots
    }

    this.addToCache(cacheKey, response)
    this.updateStats(tokenCount)

    return response
  }

  async chat(messages: Array<{ role: string; content: string }>, options?: {
    stream?: boolean
    onToken?: (token: string) => void
    sessionId?: string
  }): Promise<AIResponse> {
    if (options?.sessionId) {
      this.updateContext(options.sessionId, messages)
    }

    const prompt = this.formatChatPrompt(messages)
    return this.generate(prompt, {
      stream: options?.stream,
      onToken: options?.onToken
    })
  }

  async analyzeData(data: any, analysisPrompt: string): Promise<AIResponse> {
    const dataStr = JSON.stringify(data, null, 2)
    const prompt = `
你是一位专业的数据分析专家，请基于以下数据进行深入分析。

数据:
${dataStr}

分析要求:
${analysisPrompt}

请提供详细的分析报告，包括：
1. 关键发现
2. 趋势分析
3. 数据洞察
4. 改进建议
5. 风险提示（如适用）
`.trim()

    return this.generate(prompt, { maxTokens: 1500, temperature: 0.5 })
  }

  async generateReport(data: any, type: string = 'summary'): Promise<AIResponse> {
    const templates: Record<string, string> = {
      summary: '综合分析报告',
      sales: '销售数据报告',
      user: '用户行为报告',
      performance: '性能分析报告',
      security: '安全分析报告',
      finance: '财务分析报告',
      marketing: '营销分析报告',
      operations: '运营分析报告',
      customer: '客户分析报告',
      product: '产品分析报告'
    }

    const title = templates[type] || templates.summary
    const dataStr = typeof data === 'string' ? data : JSON.stringify(data, null, 2)

    const prompt = `
你是一位专业的报告生成专家，请生成一份${title}。

数据:
${dataStr}

请按照以下格式生成报告:

# ${title}

## 一、执行摘要
[简洁明了的总结]

## 二、关键发现
- [发现1]
- [发现2]
- [发现3]

## 三、详细分析
[深入分析内容]

## 四、趋势预测
[预测未来趋势]

## 五、建议方案
[具体可行的建议]

## 六、风险提示
[潜在风险和注意事项]
`.trim()

    return this.generate(prompt, { maxTokens: 2000, temperature: 0.4 })
  }

  async summarizeText(text: string, maxLength: number = 300): Promise<AIResponse> {
    const prompt = `
请将以下文本进行简明扼要的总结，保持核心信息完整，不超过${maxLength}字。

原文:
${text}

总结:
`.trim()

    return this.generate(prompt, { maxTokens: Math.min(maxLength * 2, 600), temperature: 0.3 })
  }

  async extractKeywords(text: string, count: number = 10): Promise<AIResponse> {
    const prompt = `
请从以下文本中提取${count}个最重要的关键词，用逗号分隔。

文本:
${text}

关键词:
`.trim()

    const response = await this.generate(prompt, { maxTokens: 200, temperature: 0.2 })
    return response
  }

  async translateText(text: string, targetLang: string = '中文'): Promise<AIResponse> {
    const prompt = `
请将以下文本翻译成${targetLang}，保持原意和语气。

原文:
${text}

翻译:
`.trim()

    return this.generate(prompt, { maxTokens: text.length * 2, temperature: 0.3 })
  }

  async classifyText(text: string, categories: string[]): Promise<AIResponse> {
    const prompt = `
请将以下文本分类到以下类别之一：${categories.join('、')}

文本:
${text}

分类结果:
`.trim()

    return this.generate(prompt, { maxTokens: 100, temperature: 0.1 })
  }

  async extractEntities(text: string): Promise<AIResponse> {
    const prompt = `
请从以下文本中提取实体，包括人名、地名、组织机构、时间、数字等，以JSON格式输出。

文本:
${text}

实体JSON:
`.trim()

    return this.generate(prompt, { maxTokens: 500, temperature: 0.2 })
  }

  async rewriteText(text: string, style: string = 'formal'): Promise<AIResponse> {
    const prompt = `
请将以下文本改写成${style}风格。

原文:
${text}

改写结果:
`.trim()

    return this.generate(prompt, { maxTokens: text.length * 2, temperature: 0.5 })
  }

  async recognizeIntent(text: string): Promise<IntentResult> {
    let bestIntent = 'unknown'
    let bestConfidence = 0
    const slots: Record<string, any> = {}

    for (const [intent, pattern] of Object.entries(this.intentPatterns)) {
      const match = text.match(pattern.regex)
      if (match) {
        const confidence = match[0].length / text.length
        if (confidence > bestConfidence) {
          bestConfidence = confidence
          bestIntent = intent
          
          for (let i = 0; i < pattern.slots.length && i < match.length - 1; i++) {
            slots[pattern.slots[i]] = match[i + 1]
          }
        }
      }
    }

    if (bestConfidence < 0.3 && this.isLoaded) {
      return await this.recognizeIntentWithModel(text, { intent: bestIntent, confidence: bestConfidence, slots, resolvedText: text })
    }

    return {
      intent: bestIntent,
      confidence: bestConfidence,
      slots,
      resolvedText: text
    }
  }

  private async recognizeIntentWithModel(text: string, fallback: IntentResult): Promise<IntentResult> {
    try {
      const intentList = Object.keys(this.intentPatterns).join('、')
      const prompt = `
请分析以下用户输入的意图，并从以下选项中选择最合适的意图：${intentList}。

用户输入:
${text}

请输出JSON格式的结果，包含以下字段：
- intent: 识别到的意图名称
- confidence: 置信度（0-1之间）
- slots: 提取到的关键信息（JSON对象）

示例输出:
{"intent": "greet", "confidence": 0.9, "slots": {}}
`.trim()

      const response = await this.generate(prompt, { maxTokens: 200, temperature: 0.1, skipCache: true })
      
      try {
        const jsonMatch = response.text.match(/\{[\s\S]*\}/)
        if (jsonMatch) {
          const result = JSON.parse(jsonMatch[0])
          return {
            intent: result.intent || fallback.intent,
            confidence: Math.max(result.confidence || 0.3, fallback.confidence),
            slots: result.slots || fallback.slots,
            resolvedText: text
          }
        }
      } catch {
        console.warn('Failed to parse model intent result, using fallback')
      }
    } catch (error) {
      console.warn('Model intent recognition failed:', error)
    }
    
    return fallback
  }

  createContext(sessionId: string): void {
    if (!this.contexts.has(sessionId)) {
      this.contexts.set(sessionId, {
        sessionId,
        messages: [],
        createdAt: Date.now(),
        lastAccessed: Date.now()
      })
    }
  }

  getContext(sessionId: string): Array<{ role: string; content: string }> | null {
    const context = this.contexts.get(sessionId)
    if (context) {
      context.lastAccessed = Date.now()
      return context.messages
    }
    return null
  }

  updateContext(sessionId: string, messages: Array<{ role: string; content: string }>): void {
    let context = this.contexts.get(sessionId)
    if (!context) {
      this.createContext(sessionId)
      context = this.contexts.get(sessionId)!
    }

    context.messages.push(...messages)
    
    if (context.messages.length > this.config.contextWindowSize) {
      context.messages = context.messages.slice(-this.config.contextWindowSize)
    }

    context.lastAccessed = Date.now()
    this.savePersistentContexts()
  }

  clearContext(sessionId: string): void {
    this.contexts.delete(sessionId)
    this.savePersistentContexts()
  }

  cleanupOldContexts(maxAge: number = 24 * 60 * 60 * 1000): void {
    const now = Date.now()
    for (const [sessionId, context] of this.contexts.entries()) {
      if (now - context.lastAccessed > maxAge) {
        this.contexts.delete(sessionId)
      }
    }
    this.savePersistentContexts()
  }

  private formatChatPrompt(messages: Array<{ role: string; content: string }>): string {
    const systemMessage = messages.find(m => m.role === 'system')?.content || 
      '你是谐伴AI助手，一个专业的后台管理系统智能助手。请使用中文进行回复，保持友好、专业、简洁的语气。'
    
    const conversationHistory = messages
      .filter(m => m.role !== 'system')
      .map(m => `${m.role === 'user' ? '用户' : '助手'}: ${m.content}`)
      .join('\n\n')

    return `
${systemMessage}

对话历史:
${conversationHistory}

请根据以上对话历史，提供合适的回复。

助手:
`.trim()
  }

  private generateCacheKey(prompt: string, options: any): string {
    const key = JSON.stringify({ prompt, options })
    let hash = 0
    for (let i = 0; i < key.length; i++) {
      const char = key.charCodeAt(i)
      hash = ((hash << 5) - hash) + char
      hash = hash & hash
    }
    return 'ai_' + Math.abs(hash).toString(36)
  }

  private addToCache(key: string, response: AIResponse): void {
    if (this.responseCache.size >= this.config.cacheSize) {
      const oldestKey = Array.from(this.responseCache.entries())
        .sort((a, b) => a[1].timestamp - b[1].timestamp)[0][0]
      this.responseCache.delete(oldestKey)
    }

    this.responseCache.set(key, {
      timestamp: Date.now(),
      data: { ...response, cached: true },
      ttl: 30 * 60 * 1000
    })

    this.savePersistentCache()
  }

  private getFromCache(key: string): AIResponse | null {
    const entry = this.responseCache.get(key)
    if (entry && Date.now() - entry.timestamp < entry.ttl) {
      return entry.data
    }
    if (entry) {
      this.responseCache.delete(key)
    }
    return null
  }

  private loadPersistentCache(): void {
    if (!this.config.enablePersistentCache) return
    
    try {
      const cached = localStorage.getItem('local_ai_cache')
      if (cached) {
        const data = JSON.parse(cached)
        const now = Date.now()
        
        for (const [key, entry] of Object.entries(data)) {
          const cacheEntry = entry as CacheEntry
          if (now - cacheEntry.timestamp < cacheEntry.ttl) {
            this.responseCache.set(key, cacheEntry)
          }
        }
      }
    } catch (error) {
      console.warn('Failed to load persistent cache:', error)
    }
  }

  private savePersistentCache(): void {
    if (!this.config.enablePersistentCache) return
    
    try {
      const data = Object.fromEntries(this.responseCache.entries())
      localStorage.setItem('local_ai_cache', JSON.stringify(data))
    } catch (error) {
      console.warn('Failed to save persistent cache:', error)
    }
  }

  private loadPersistentContexts(): void {
    try {
      const cached = localStorage.getItem('local_ai_contexts')
      if (cached) {
        const data = JSON.parse(cached)
        for (const [sessionId, entry] of Object.entries(data)) {
          this.contexts.set(sessionId, entry as ContextEntry)
        }
      }
    } catch (error) {
      console.warn('Failed to load persistent contexts:', error)
    }
  }

  private savePersistentContexts(): void {
    try {
      const data = Object.fromEntries(this.contexts.entries())
      localStorage.setItem('local_ai_contexts', JSON.stringify(data))
    } catch (error) {
      console.warn('Failed to save persistent contexts:', error)
    }
  }

  private updateStats(tokenCount: number): void {
    this.totalRequests++
    this.totalTokensProcessed += tokenCount
    
    const now = Date.now()
    this.recentRequests.push({ timestamp: now, tokens: tokenCount })
    
    const tenMinutesAgo = now - 10 * 60 * 1000
    this.recentRequests = this.recentRequests.filter(r => r.timestamp > tenMinutesAgo)
  }

  getModelInfo(): ModelInfo {
    return {
      name: this.config.modelName,
      size: '~1.2GB (量化后)',
      isLoaded: this.isLoaded,
      isLoading: this.isLoading,
      loadedAt: this.modelLoadTime ? new Date(Date.now() - this.modelLoadTime) : undefined,
      lastUsed: this.totalRequests > 0 ? new Date() : undefined
    }
  }

  getStats(): {
    totalRequests: number
    totalTokens: number
    cacheSize: number
    recentRequests: number
    avgResponseTime?: number
    activeContexts: number
  } {
    const cached = Array.from(this.responseCache.values())
    const responseTimes = cached.filter(e => e.data.responseTime).map(e => e.data.responseTime)
    const avgResponseTime = responseTimes.length > 0 
      ? responseTimes.reduce((a, b) => a + b, 0) / responseTimes.length 
      : undefined

    return {
      totalRequests: this.totalRequests,
      totalTokens: this.totalTokensProcessed,
      cacheSize: this.responseCache.size,
      recentRequests: this.recentRequests.length,
      avgResponseTime,
      activeContexts: this.contexts.size
    }
  }

  clearCache(): void {
    this.responseCache.clear()
    this.savePersistentCache()
    console.log('AI cache cleared')
  }

  unloadModel(): void {
    if (this.model) {
      this.model.dispose()
      this.model = null
    }
    this.tokenizer = null
    this.isLoaded = false
    this.modelLoadTime = null
    console.log('Local AI model unloaded')
  }

  /**
   * 通用嵌入（文本→向量）：默认用 token embed 平均，避免重模型
   */
  async embed(text: string): Promise<number[]> {
    if (!this.tokenizer) {
      // Fallback：shared 层的 n-gram 向量（导出给知识库）
      try {
        const _kbPath = '../../../shared/knowledgeBaseManager.js'
        const bridge = await import(_kbPath)
        const inst = bridge?.knowledgeBaseManager || bridge?.default?.knowledgeBaseManager
        if (inst && typeof inst._embed === 'function') {
          const m = inst._embed(text)
          const arr: number[] = []
          for (const v of (m as Map<any, number>).values()) arr.push(v)
          if (arr.length === 0) return [0]
          while (arr.length < 384) arr.push(0)
          return arr.slice(0, 384)
        }
      } catch (_) {
        // ignore import failure
      }
      return [0]
    }
    const enc = await this.tokenizer(text, { truncation: true, max_length: 512 })
    const ids = Array.isArray(enc.input_ids) ? enc.input_ids : []
    const dim = 384
    const vec = new Array(dim).fill(0)
    ids.forEach((tok: number, i: number) => { vec[(tok * (i + 1)) % dim] += 1 })
    let norm = 0; for (const v of vec) norm += v * v
    norm = Math.sqrt(norm) || 1
    return vec.map(v => v / norm)
  }
}

export const localAIEngine = new LocalAIEngine()